import React from 'react';
import { Palette, Loader2, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const BrandValidationPanel = ({ brandGuidelines, brandValidation, validatingBrand, content }) => {
  if (brandGuidelines.length === 0) return null;

  return (
    <Card className={`border-slate-100 ${
      brandValidation?.valid === false 
        ? 'border-red-200 bg-red-50/30' 
        : brandValidation?.valid === true 
          ? 'border-emerald-200 bg-emerald-50/30'
          : ''
    }`} data-testid="brand-validation-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Palette className="h-4 w-4 text-purple-500" />
            Brand Check
          </CardTitle>
          {validatingBrand ? (
            <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
          ) : brandValidation ? (
            brandValidation.valid ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            )
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {!content ? (
          <p className="text-sm text-slate-500">Start typing to check brand compliance</p>
        ) : brandValidation ? (
          <>
            <div className={`p-2 rounded-lg text-sm flex items-center gap-2 ${
              brandValidation.valid 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              {brandValidation.valid ? (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Content follows brand guidelines
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  {brandValidation.warnings?.length || 0} issue(s) found
                </>
              )}
            </div>

            {brandValidation.warnings?.length > 0 && (
              <div className="space-y-2">
                {brandValidation.warnings.slice(0, 3).map((warning, i) => (
                  <div 
                    key={`warning-${warning.type}-${i}`}
                    className={`p-2 rounded text-xs ${
                      warning.severity === 'error' 
                        ? 'bg-red-50 text-red-700 border border-red-200' 
                        : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}
                  >
                    <span className="font-medium capitalize">{warning.type}:</span> {warning.message}
                  </div>
                ))}
                {brandValidation.warnings.length > 3 && (
                  <p className="text-xs text-slate-500">
                    +{brandValidation.warnings.length - 3} more issues
                  </p>
                )}
              </div>
            )}

            {brandValidation.suggestions?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 font-medium">Suggestions:</p>
                {brandValidation.suggestions.slice(0, 2).map((suggestion, i) => (
                  <div key={`suggestion-${i}`} className="p-2 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200 flex items-start gap-2">
                    <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    {suggestion.message}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t border-slate-100">
              <span>{brandValidation.character_count} chars</span>
              <span>{brandValidation.hashtag_count} hashtags</span>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-500">Checking...</p>
        )}
      </CardContent>
    </Card>
  );
};

export default BrandValidationPanel;
